import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { NotesSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/notes/NotesSupportingItemInput'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a notes supporting item.
 */
export class NotesRequestedSupportingItemViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * @see {next$}
   * @private
   */
  private readonly _nextSubject = new Subject<unknown>()

  /**
   * Emits when we intend to navigate to the next item.
   */
  readonly next$ = this._nextSubject.asObservable()

  /**
   * The supporting item input type.
   */
  readonly type = 'Notes' as const

  /**
   * Modal for editing notes.
   */
  readonly dialog = new ModalViewModel()

  /**
   * The edited text value.
   */
  @observable accessor editedText = ''

  constructor(
    private readonly _job: SubmittedJob,
    public readonly item: RequestedSupportingItem<NotesSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
  ) {
    this.editedText = this.item.input.text
  }

  /**
   * Whether the item is read-only.
   */
  @computed get isReadOnly() {
    return this._job.status !== 'InfoGathering'
  }

  /**
   * Whether the item is considered completed.
   */
  @computed get isCompleted() {
    return this.item.isCompleted
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets the edited text value.
   */
  @action.bound
  setEditedText(text: string) {
    this.editedText = text
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog() {
    this.editedText = this.item.input.text
    this.dialog.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  closeDialog() {
    this.dialog.close()
  }

  /**
   * Fetches any required data.
   */
  fetch = task(async (_signal: AbortSignal) => {
    // Notes don't need to fetch anything external
    // This is here for compatibility with the view model interface
  })

  /**
   * Saves the notes.
   */
  save = task.resolved(async () => {
    await this._infoGatheringStore.provideSupportingItemNotes(
      this._job.id,
      this.item,
      this.editedText,
    )
    this.closeDialog()
    this._nextSubject.next(undefined)
  })
}
